import { useEffect, useRef, useState } from "react";
import { useGame } from "@/hooks/useGame";
import { useTheme } from "@/hooks/useTheme";
import {
  DEFAULT_PARTY_NAMES,
  computeScore,
  getEventById,
  timeOfDayForDay,
} from "@/game/engine";
import { DIFFICULTIES, LANDMARKS, TRADE_PRICES } from "@/game/data";
import type { Difficulty } from "@/game/types";
import { formatSats } from "@/game/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Moon, Sun } from "lucide-react";

import { StatusBar } from "./StatusBar";
import { TrailMap } from "./TrailMap";
import { TravelScene } from "./TravelScene";
import { WagonIcon } from "./WagonIcon";
import { PartyPanel } from "./PartyPanel";
import { GameLog } from "./GameLog";
import { Controls } from "./Controls";
import { TradingScreen } from "./TradingScreen";
import { StackSatsGame } from "./StackSatsGame";
import { Leaderboard } from "./Leaderboard";
import { PublishRun } from "./PublishRun";
import { LoginArea } from "@/components/auth/LoginArea";
import { Trophy, ArrowLeft } from "lucide-react";

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label="Toggle theme"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
    >
      {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
    </Button>
  );
}

export function SatoshiTrail() {
  const game = useGame();
  const { state } = game;
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur">
        <div className="container flex items-center justify-between gap-3 py-4">
          <button
            className="flex items-center gap-3 text-left"
            onClick={() => setShowLeaderboard(false)}
          >
            <WagonIcon className="h-8 w-11" />
            <div>
              <h1 className="font-pixel text-sm text-primary sm:text-base">
                BCH Trail
              </h1>
              <p className="hidden font-terminal text-base text-muted-foreground sm:block">
                A Bitcoin Cash pioneer adventure
              </p>
            </div>
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="font-terminal text-base"
              onClick={() => setShowLeaderboard((v) => !v)}
            >
              <Trophy className="size-4" />
              <span className="hidden sm:inline">Leaderboard</span>
            </Button>
            <LoginArea className="max-w-44" />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="container max-w-4xl py-6">
        {showLeaderboard ? (
          <LeaderboardScreen onBack={() => setShowLeaderboard(false)} />
        ) : (
          <>
            {state.phase === "menu" && (
              <MenuScreen game={game} onLeaderboard={() => setShowLeaderboard(true)} />
            )}
            {state.phase === "outfitting" && <OutfittingScreen game={game} />}
            {(state.phase === "traveling" ||
              state.phase === "event" ||
              state.phase === "landmark" ||
              state.phase === "trading" ||
              state.phase === "minigame") && <TravelScreen game={game} />}
            {state.phase === "victory" && (
              <EndScreen game={game} victory onLeaderboard={() => setShowLeaderboard(true)} />
            )}
            {state.phase === "gameover" && (
              <EndScreen game={game} victory={false} onLeaderboard={() => setShowLeaderboard(true)} />
            )}
          </>
        )}
      </main>

      <footer className="border-t py-6 text-center">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noreferrer"
          className="font-terminal text-lg text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </div>
  );
}

type GameApi = ReturnType<typeof useGame>;

function LeaderboardScreen({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        className="font-terminal text-base"
        onClick={onBack}
      >
        <ArrowLeft className="size-4" /> Back
      </Button>
      <Card>
        <CardContent className="p-5">
          <Leaderboard />
        </CardContent>
      </Card>
    </div>
  );
}

function MenuScreen({
  game,
  onLeaderboard,
}: {
  game: GameApi;
  onLeaderboard: () => void;
}) {
  return (
    <div className="scanlines relative overflow-hidden rounded-xl border-2 border-primary/50 bg-gradient-to-b from-secondary/40 to-background p-8 text-center sm:p-12">
      <div className="mx-auto max-w-2xl space-y-6">
        <WagonIcon className="mx-auto h-20 w-28 sm:h-24 sm:w-32" />
        <h2 className="font-pixel text-lg leading-relaxed text-primary sm:text-2xl">
          BCH TRAIL
        </h2>
        <p className="font-terminal text-xl leading-snug sm:text-2xl">
          The year is 1849... no wait, it's the future. Load up your wagon,
          guard your seed phrase, and lead your party across the frontier toward{" "}
          <span className="text-primary">Freedom City</span> — a town running
          entirely on peer-to-peer electronic cash.
        </p>
        <p className="font-terminal text-lg text-muted-foreground">
          Manage your sats, food, and health. Survive volatility storms, dodge
          scammers, and stack sats along the way. Can you make it 1,700 miles?
        </p>
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            size="lg"
            className="font-pixel text-xs"
            onClick={() => game.start(DEFAULT_PARTY_NAMES)}
          >
            Start the Journey
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="font-pixel text-xs"
            onClick={onLeaderboard}
          >
            <Trophy className="size-4" /> Leaderboard
          </Button>
        </div>
        <p className="font-terminal text-base text-muted-foreground blink">
          Press start to hit the trail_
        </p>
      </div>
    </div>
  );
}

function OutfittingScreen({ game }: { game: GameApi }) {
  const { state } = game;
  const [names, setNames] = useState<string[]>(
    state.party.map((p) => p.name),
  );
  const [difficulty, setDifficulty] = useState<Difficulty>(state.difficulty);
  const cfg = DIFFICULTIES[difficulty];

  const updateName = (i: number, value: string) => {
    setNames((prev) => prev.map((n, idx) => (idx === i ? value : n)));
  };

  const commitAndBegin = () => {
    // restart with edited names + chosen difficulty to set fresh supplies
    game.start(names, difficulty);
    // move straight into the journey
    setTimeout(() => game.beginJourney(), 0);
  };

  return (
    <div className="space-y-6">
      <Card className="pixel-panel border-none">
        <CardContent className="space-y-5 p-6">
          <h2 className="font-pixel text-sm text-primary">Outfit Your Party</h2>

          {/* Difficulty selector */}
          <div className="space-y-2">
            <h3 className="font-pixel text-[9px] uppercase tracking-wider text-muted-foreground">
              Difficulty
            </h3>
            <div className="grid gap-2 sm:grid-cols-3">
              {(Object.keys(DIFFICULTIES) as Difficulty[]).map((key) => {
                const d = DIFFICULTIES[key];
                const selected = key === difficulty;
                return (
                  <button
                    key={key}
                    onClick={() => setDifficulty(key)}
                    className={cn(
                      "rounded-md border p-3 text-left transition-colors",
                      "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      selected
                        ? "border-primary bg-primary/10"
                        : "border-border bg-card hover:bg-accent/20",
                    )}
                  >
                    <div className="font-terminal text-xl leading-none">
                      {d.emoji} {d.label}
                    </div>
                    <div className="mt-1 font-terminal text-sm text-muted-foreground leading-tight">
                      {d.blurb}
                    </div>
                    <div className="mt-1 font-pixel text-[8px] text-primary">
                      {d.scoreMult}× score
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <p className="font-terminal text-lg text-muted-foreground">
            Name your fellow pioneers. On{" "}
            <span className="text-primary">{cfg.label}</span> you'll start with{" "}
            <span className="text-primary">{formatSats(cfg.startingInventory.sats)} sats</span>,{" "}
            {cfg.startingInventory.food} food, {cfg.startingInventory.medicine} med kits, and{" "}
            {cfg.startingInventory.gear} gear.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            {names.map((name, i) => (
              <div key={i} className="space-y-1">
                <Label className="font-terminal text-base">
                  {i === 0 ? "Wagon Leader" : `Companion ${i}`}
                </Label>
                <Input
                  value={name}
                  maxLength={14}
                  onChange={(e) => updateName(i, e.target.value)}
                  className="font-terminal text-lg"
                />
              </div>
            ))}
          </div>

          <Button
            size="lg"
            className="w-full font-pixel text-xs"
            onClick={commitAndBegin}
          >
            Head Out on the Trail
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function TravelScreen({ game }: { game: GameApi }) {
  const { state } = game;
  const landmark = LANDMARKS[state.currentLandmarkIndex];

  // Kick off a short "rolling" burst whenever the day advances so the wagon
  // visibly travels for a moment after each step, then settles.
  const [rolling, setRolling] = useState(false);
  const lastDay = useRef(state.day);
  useEffect(() => {
    if (state.day !== lastDay.current) {
      lastDay.current = state.day;
      setRolling(true);
      const t = setTimeout(() => setRolling(false), 1600);
      return () => clearTimeout(t);
    }
  }, [state.day]);

  // The wagon keeps rolling while actively traveling, and briefly after each
  // step; it settles (idle sway) during events, landmarks, trading, mini-game.
  const moving = rolling || state.phase === "traveling";

  // Biome for the current leg: the one you're heading toward (or the current
  // landmark's biome when parked at one).
  const legLandmark =
    state.phase === "landmark"
      ? LANDMARKS[state.currentLandmarkIndex]
      : LANDMARKS[Math.min(state.currentLandmarkIndex + 1, LANDMARKS.length - 1)];

  return (
    <div className="space-y-5">
      <StatusBar state={state} />

      <Card>
        <CardContent className="space-y-5 p-5">
          <TravelScene
            moving={moving}
            pace={state.pace}
            weather={state.weather}
            biome={legLandmark.biome}
            timeOfDay={timeOfDayForDay(state.day)}
          />
          <TrailMap state={state} />

          {/* Phase-specific center content */}
          {state.phase === "traveling" && (
            <TravelingActions game={game} />
          )}
          {state.phase === "event" && state.activeEventId && (
            <EventCard game={game} />
          )}
          {state.phase === "landmark" && (
            <LandmarkCard game={game} landmarkIndex={state.currentLandmarkIndex} />
          )}
          {state.phase === "trading" && (
            <TradingScreen
              state={state}
              onTrade={game.trade}
              onClose={game.closeTrading}
            />
          )}
          {state.phase === "minigame" && (
            <div className="space-y-3">
              <h3 className="font-pixel text-sm text-primary">
                ⚡ Stack Sats — {landmark.name}
              </h3>
              <StackSatsGame onFinish={game.finishMinigame} />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <PartyPanel party={state.party} />
        <GameLog log={state.log} />
      </div>

      <div className="text-center">
        <button
          onClick={game.reset}
          className="font-terminal text-base text-muted-foreground underline-offset-4 hover:text-destructive hover:underline"
        >
          Abandon journey & reset
        </button>
      </div>
    </div>
  );
}

function TravelingActions({ game }: { game: GameApi }) {
  const { state } = game;
  return (
    <div className="space-y-4">
      <Controls
        pace={state.pace}
        rations={state.rations}
        onPace={game.setPace}
        onRations={game.setRations}
      />
      <Button size="lg" className="w-full font-pixel text-xs" onClick={game.travel}>
        Continue on the Trail →
      </Button>
    </div>
  );
}

function EventCard({ game }: { game: GameApi }) {
  const event = getEventById(game.state.activeEventId);
  if (!event) return null;
  return (
    <div className="rounded-lg border-2 border-accent/60 bg-accent/10 p-5">
      <div className="mb-2 text-4xl" aria-hidden>{event.emoji}</div>
      <h3 className="font-pixel text-sm text-accent-foreground">{event.title}</h3>
      <p className="mt-2 font-terminal text-xl leading-snug">{event.description}</p>
      <div className="mt-4 grid gap-2">
        {event.choices.map((c, i) => (
          <Button
            key={i}
            variant={i === 0 ? "default" : "secondary"}
            className="w-full justify-start font-terminal text-lg"
            onClick={() => game.chooseEvent(i)}
          >
            {c.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function LandmarkCard({
  game,
  landmarkIndex,
}: {
  game: GameApi;
  landmarkIndex: number;
}) {
  const landmark = LANDMARKS[landmarkIndex];
  return (
    <div className="rounded-lg border-2 border-primary/50 bg-primary/5 p-5">
      <div className="mb-2 text-4xl" aria-hidden>📍</div>
      <h3 className="font-pixel text-sm text-primary">{landmark.name}</h3>
      <p className="mt-2 font-terminal text-xl leading-snug">{landmark.description}</p>

      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {landmark.hasTrader && (
          <Button
            variant="secondary"
            className="font-terminal text-lg"
            onClick={game.openTrading}
          >
            🛒 Visit Trading Post
          </Button>
        )}
        <Button
          variant="outline"
          className="font-terminal text-lg"
          onClick={game.openMinigame}
        >
          ⚡ Stack Sats (mini-game)
        </Button>
        <Button
          className="font-terminal text-lg sm:col-span-2"
          onClick={game.continueFromLandmark}
        >
          Continue on the Trail →
        </Button>
      </div>
      <p className="mt-2 font-terminal text-sm text-muted-foreground">
        Buy prices shown in sats — food {TRADE_PRICES.food}, med {TRADE_PRICES.medicine}, gear {TRADE_PRICES.gear}.
      </p>
    </div>
  );
}

function EndScreen({
  game,
  victory,
  onLeaderboard,
}: {
  game: GameApi;
  victory: boolean;
  onLeaderboard: () => void;
}) {
  const { state } = game;
  const score = computeScore(state);
  const survivors = state.party.filter((p) => p.alive);

  return (
    <div className="scanlines relative overflow-hidden rounded-xl border-2 border-primary/50 bg-gradient-to-b from-secondary/40 to-background p-6 text-center sm:p-10">
      <div className="mx-auto max-w-2xl space-y-5">
        <div className="text-6xl" aria-hidden>{victory ? "🎉⚡" : "🪦"}</div>
        <h2 className="font-pixel text-lg text-primary sm:text-2xl">
          {victory ? "YOU REACHED FREEDOM CITY!" : "THE TRAIL CLAIMED YOU"}
        </h2>
        <p className="font-terminal text-xl leading-snug">
          {victory
            ? `After ${state.day} days and ${Math.round(state.distance)} miles, your party arrived in Freedom City with ${formatSats(state.inventory.sats)} sats to spend in the circular economy.`
            : `Your party could not survive the journey. The wagon sits abandoned after ${state.day} days on the trail.`}
        </p>

        <div className="rounded-lg border bg-card p-4 text-left font-terminal text-lg">
          <div className="mb-2 font-pixel text-[10px] uppercase tracking-wider text-muted-foreground">
            Final Tally
          </div>
          <ul className="space-y-1">
            <li>
              Difficulty: {DIFFICULTIES[state.difficulty].emoji}{" "}
              {DIFFICULTIES[state.difficulty].label}
            </li>
            <li>Days on trail: {state.day}</li>
            <li>Sats remaining: {formatSats(state.inventory.sats)}</li>
            <li>
              Survivors: {survivors.length}/{state.party.length}
              {survivors.length ? ` — ${survivors.map((s) => s.name).join(", ")}` : ""}
            </li>
            <li className="text-primary">Final Score: {formatSats(score)}</li>
          </ul>
        </div>

        {/* Submit to the global Nostr leaderboard */}
        <PublishRun state={state} />

        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button size="lg" className="font-pixel text-xs" onClick={game.reset}>
            Play Again
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="font-pixel text-xs"
            onClick={onLeaderboard}
          >
            <Trophy className="size-4" /> View Leaderboard
          </Button>
        </div>
      </div>
    </div>
  );
}
